import type { Request } from 'express';
import type { JSONSchema7 } from 'json-schema';
import type { ReactNode, RefObject } from 'react';
import type { DropdownItemProps } from 'semantic-ui-react';
import type { JsonValue } from 'type-fest';

import type { DateRangeValue } from '@components/date-range';

export { FilterOperator, SortOrder } from '@/api/exp';
export type { PaginationInfo } from '@/api/v2';

export type SemanticDropdownItemProps = DropdownItemProps;

export type ServiceRequest = Request;

export type ServiceRequestOptions = RequestCustom & Pick<Request, 'session'>;

export type ServiceResponseStatus = 'success' | 'error';

export interface ServiceResponse {
  status: ServiceResponseStatus;
}

export type EntityServiceResponse<
  T,
  Unwrapped extends boolean = false,
> = Unwrapped extends true
  ? ServiceResponse & { data: T }
  : // TODO: This `body` seems unnecessary and confusing. Consider removing it.
    ServiceResponse & { body: { data: T } };

export type AbstractServiceResponse<T> =
  ServiceResponse | EntityServiceResponse<T, boolean>;

export type { FeedMetadata, ServiceList } from '@/api/api';

export interface ServiceError {
  statusCode?: number;
  message?: string;
  type?: string;
}

export type MaybeSerializedQueryValue<
  T,
  IsSerialized extends boolean = false,
> = IsSerialized extends true ? string : T;

export type PaginationSort<
  Key extends string = string,
  IsDescending extends string | boolean = boolean,
> = Partial<Record<Key, IsDescending>>;

/**
 * Pagination params that can represent serialized or deserialized variants. By
 * default it represents the deserialized interface. Setting `IsSerialized` will
 * set all of the types to strings.
 */
export type PaginationParamsBase<
  IsSerialized extends boolean = false,
  SortableFieldName extends string = string,
> = {
  filter?: MaybeSerializedQueryValue<string, IsSerialized>;
  limit?: MaybeSerializedQueryValue<number, IsSerialized>;
  page?: MaybeSerializedQueryValue<number, IsSerialized>;
  sort?: PaginationSort<
    SortableFieldName,
    MaybeSerializedQueryValue<boolean, IsSerialized>
  >;
};

/**
 * Proper representation of query params, usually on the client, or possibly on
 * the server if coerced.
 */
export type PaginationParams<SortableFieldName extends string = string> =
  PaginationParamsBase<false, SortableFieldName>;

export type PaginationQueryParams = Pick<PaginationParams, 'page' | 'limit'>;

export interface PlatformPaginationQueryParams<
  SortableFieldName extends string = string,
> {
  page?: number;
  limit?: number;
  order_by?: SortableFieldName[];
  order_by_descending?: boolean[];
}

export type OmitPlatformPaginationParams<Params> = Omit<
  Params,
  keyof PlatformPaginationQueryParams
>;

export const initialPaginationQueryParams = Object.freeze<
  Required<PaginationQueryParams>
>({
  page: 1,
  limit: 10,
});

export type ListRequestQueryParams<SortableFieldName extends string = string> =
  Pick<PaginationParams<SortableFieldName>, 'filter' | 'sort'>;

/**
 * A serialized representation of query params on the server, where the query
 * is parsed to an object automatically from the URL, but only as string values.
 */
export type SerializedPaginationParams<
  SortableFieldName extends string = string,
> = PaginationParamsBase<true, SortableFieldName>;

export type DataUpdateType = 'create' | 'update' | 'delete';

export type TotalRows = {
  totalRows: number;
};

export type PaginatedEntityResponse<
  T,
  Unwrapped extends boolean = false,
> = EntityServiceResponse<T, Unwrapped> & TotalRows;

export type CursorPaginatedEntityResponse<T> = EntityServiceResponse<
  T,
  true
> & {
  itemCount: number;
  nextCursor: string | null;
};

export interface DataUpdateEvent {
  entity: string;
  entityId: string;
  updateType: DataUpdateType;
  originatingSocketId: string;
}

/**
 * Data entity fetched from the backend
 */
export type DataEntity<T, K extends string = string, D = Record<K, T>> = {
  isLoading: boolean;
  isUpdating: boolean;
  data: D | null;
  error?: ServiceError;
  stale?: Record<K, DataUpdateType>;
};

export const nullDataEntity: Readonly<DataEntity<any, string, null>> =
  Object.freeze({
    isLoading: false,
    isUpdating: false,
    data: null,
  });

export type RestVerb = 'get' | 'post' | 'put' | 'delete' | 'patch';

export type AsyncStatus =
  | 'initial'
  | 'loading'
  | 'creating'
  | 'updating'
  | 'deleting'
  | 'ready'
  | 'error';

export interface AsyncState<ErrorType = ServiceError> {
  status: AsyncStatus;
  error?: ErrorType;
  stale?: boolean;
}

export type WithAsyncState<
  T,
  ErrorType = ServiceError,
> = AsyncState<ErrorType> & T;

export type StatusEntity<T> = WithAsyncState<{ data: T | null }>;

export const nullStatusEntity: Readonly<StatusEntity<any>> = Object.freeze({
  status: 'initial',
  data: null,
});

export type EntityActionParams<T = unknown> = T extends void | undefined
  ? T
  : {
      showToastOnSuccess?: boolean;
      showToastOnFailure?: boolean;
    } & T;

export interface TableViewProps<SortableFieldName extends string = string> {
  page: number;
  pageSize: number;
  filterStr: string;
  sort?: PaginationSort<SortableFieldName>;
}

export type TableViewPropsSingleColumnSort = Omit<TableViewProps, 'sort'> & {
  sort: {
    id: string;
    desc?: boolean;
  };
};

export const initialTableViewProps: Readonly<Omit<TableViewProps, 'sort'>> =
  Object.freeze({
    page: 0,
    pageSize: 10,
    filterStr: '',
  });

export type FieldType =
  | 'String'
  | 'StringList'
  | 'Enum'
  | 'EnumList'
  | 'Boolean'
  | 'DateTime'
  | 'TimePeriod'
  | 'Int'
  | 'BigInt'
  | 'Float'
  | 'JSON'
  | 'DateRange'
  | 'KeyValue';

export type FieldEnumOption = { label: string; value: string };

export type FieldEnumOptions = string[] | FieldEnumOption[];

type FieldBase =
  | {
      typeName?: 'String';
      data?: string;
      example?: string;
    }
  | {
      typeName: 'StringList';
      data?: string[];
      example?: string[];
    }
  | {
      typeName: 'Enum';
      data?: string;
      /**
       * All available enum options for this field, to populate a dropdown list.
       * This may be undefined where the field is being rendered in readonly
       * mode, which only needs to show the current data value.
       */
      enumValues?: FieldEnumOptions;
      example?: string;
      enumDisplayTransform?: 'uppercase' | 'lowercase' | 'none';
    }
  | {
      typeName: 'EnumList';
      data?: string[];
      /**
       * All available enum options for this field, to populate a dropdown list.
       * This may be undefined where the field is being rendered in readonly
       * mode, which only needs to show the current data value.
       */
      enumValues?: FieldEnumOptions;
      example?: string[];
      enumDisplayTransform?: 'uppercase' | 'lowercase' | 'none';
    }
  | {
      typeName: 'Boolean';
      data?: boolean;
      example?: boolean;
    }
  | {
      typeName: 'DateTime';
      data?: string;
      example?: string;
    }
  | {
      typeName: 'TimePeriod';
      data?: string;
      example?: string;
    }
  | {
      typeName: 'Int';
      data?: number;
      example?: number;
    }
  | {
      typeName: 'BigInt';
      data?: number;
      example?: number;
    }
  | {
      typeName: 'Float';
      data?: number;
      example?: number;
    }
  | {
      typeName: 'JSON';
      data?: JsonValue;
      example?: JsonValue;
      isPartiallyFromEnv?: boolean; // Indicates if part of object comes from env vars
    }
  | {
      typeName: 'DateRange';
      data?: DateRangeValue;
      example?: number;
    }
  | {
      typeName: 'KeyValue';
      data?: Record<string, string>;
      example?: Record<string, string>;
      /**
       * The name of the item being edited, used in UI text.
       * @example "annotation" -> "You can add up to 5 annotations"
       * @default "item"
       */
      itemName?: string;
    };
export type FieldValue = FieldBase['data'];
export type Field<T extends PropertyKey = string> = FieldBase & {
  key: T;
  name?: string;
  category?: string;
  description?: string;
  required?: boolean;
  configSchema?: JSONSchema7; // JSON Schema for validation
  isDeprecated?: boolean;
  deprecatedReason?: string | null;
  isNullable?: boolean; // Indicates if this field accepts null values
  validationOptions?: FieldValidationOptions;
};

export type Filter<T extends PropertyKey = string> = Field<T> & {
  hidden?: boolean;
  className?: string;
};

export type FieldValidationOptions = {
  // Trim string values
  trimValues?: boolean;

  // Minimum value for numeric fields
  minValue?: number;

  // Maximum value for numeric fields
  maxValue?: number;

  // Step to increment / decrement numeric fields
  step?: number;

  // Clamp floating point values to the specified number of decimal places
  decimalPlaces?: number;
};

export type FieldValidator = (
  value: FieldValue,
  options: FieldValidationOptions | undefined,
) => any;

/**
 * Props interface for the standard field editor component
 */
export interface FieldEditorProps {
  field: Field;
  disabled?: boolean;
  selected?: boolean;
  inputId?: string;
  label?: ReactNode;
  onChange: (newValue: Field) => void;
  setEditorPopupOpen?: (open: boolean) => void;
  className?: string;
  inputClassName?: string;
  clearable?: boolean;
  validateChanges?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
  showBooleansAsToggles?: boolean;
  defaultOpen?: boolean;
}

export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;
